import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import {
  EditCommunityPostRequestModel,
  CommunityPostResponseModel,
  ProjectResponseModel,
} from '@uniquegood/realworld-admin-interface';
import { Avatar, Button, Form, Input, Modal, ModalProps, Select, Upload, message } from 'antd';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { debounce } from 'es-toolkit';
import React from 'react';
import { fileApi, communityPostApi, projectApi } from '@src/apis/admin';

interface EditPostModalProps {
  modalData: ModalProps;
  closeModal: () => unknown;
  post: CommunityPostResponseModel | null;
  onSuccess: () => void;
}

interface FormValues {
  title: string;
  content: string;
  taggedProjectId?: string;
  linkedProjectIds?: string[];
}

interface ImageItem {
  id: string;
  url: string;
  isNew: boolean;
  file?: File;
}

interface SortableImageItemProps {
  image: ImageItem;
  onRemove: (id: string) => void;
}

function SortableImageItem({ image, onRemove }: SortableImageItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: image.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    border: '1px solid #d9d9d9',
    borderRadius: 4,
    background: '#fff',
    cursor: 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <img
        src={image.url}
        alt=""
        width={100}
        height={100}
        style={{ objectFit: 'cover', borderRadius: 4, display: 'block' }}
        draggable={false}
      />
      <Button
        type="primary"
        danger
        size="small"
        icon={<DeleteOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(image.id);
        }}
        style={{
          position: 'absolute',
          top: 4,
          right: 4,
          minWidth: 24,
          width: 24,
          height: 24,
          padding: 0,
        }}
      />
      {image.isNew && (
        <div
          style={{
            position: 'absolute',
            bottom: 4,
            left: 4,
            background: '#52c41a',
            color: '#fff',
            fontSize: 10,
            padding: '1px 4px',
            borderRadius: 2,
          }}
        >
          NEW
        </div>
      )}
    </div>
  );
}

function ProjectOptionLabel({ project }: { project: ProjectResponseModel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Avatar
        src={project.verticalImageUrl}
        size={32}
        shape="square"
        style={{ flexShrink: 0 }}
      >
        {project.name?.[0]}
      </Avatar>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {project.name}
        </span>
        <span style={{ fontSize: 11, color: '#999' }}>{project.id}</span>
      </div>
    </div>
  );
}

function ImageOverlay({ image }: { image: ImageItem }) {
  return (
    <div
      style={{
        position: 'relative',
        border: '2px solid #1890ff',
        borderRadius: 4,
        background: '#fff',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}
    >
      <img
        src={image.url}
        alt=""
        width={100}
        height={100}
        style={{ objectFit: 'cover', borderRadius: 4, display: 'block' }}
        draggable={false}
      />
      {image.isNew && (
        <div
          style={{
            position: 'absolute',
            bottom: 4,
            left: 4,
            background: '#52c41a',
            color: '#fff',
            fontSize: 10,
            padding: '1px 4px',
            borderRadius: 2,
          }}
        >
          NEW
        </div>
      )}
    </div>
  );
}

export default function EditPostModal({ modalData, closeModal, post, onSuccess }: EditPostModalProps) {
  const [form] = Form.useForm<FormValues>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [images, setImages] = React.useState<ImageItem[]>([]);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [projectOptions, setProjectOptions] = React.useState<ProjectResponseModel[]>([]);
  const [isSearchingProjects, setIsSearchingProjects] = React.useState(false);

  // recruitmentStatus가 있으면 모집 게시글
  const isRecruitmentPost = !!post?.recruitmentStatus;

  const searchProjects = async (keyword: string) => {
    try {
      setIsSearchingProjects(true);
      const { data } = await projectApi.getProjects(keyword || undefined, undefined, undefined, 1, 20);
      setProjectOptions(data.data.data || []);
    } catch (e) {
      console.error(e);
      setProjectOptions([]);
    } finally {
      setIsSearchingProjects(false);
    }
  };

  const debouncedSearch = React.useMemo(
    () =>
      debounce((keyword: string) => {
        searchProjects(keyword);
      }, 300),
    []
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  React.useEffect(() => {
    if (modalData.open && post) {
      form.setFieldsValue({
        title: post.title || '',
        content: post.content || '',
        taggedProjectId: post.taggedProject?.id,
        linkedProjectIds: post.linkedProjects?.map((p) => p.id) || [],
      });
      // images 배열에서 id와 url을 가져옴
      if (post.images && post.images.length > 0) {
        setImages(
          post.images
            .filter((img) => img.id && img.url)
            .map((img) => ({
              id: img.id!,
              url: img.url!,
              isNew: false,
            }))
        );
      } else {
        setImages([]);
      }

      // 기존 프로젝트 정보를 옵션으로 설정
      const initialProjects: ProjectResponseModel[] = [];
      if (post.taggedProject?.id && post.taggedProject?.name) {
        initialProjects.push({
          id: post.taggedProject.id,
          name: post.taggedProject.name,
          verticalImageUrl: post.taggedProject.verticalImageUrl,
        });
      }
      if (post.linkedProjects && post.linkedProjects.length > 0) {
        post.linkedProjects.forEach((p) => {
          if (p.id && p.name && !initialProjects.some((ip) => ip.id === p.id)) {
            initialProjects.push({
              id: p.id,
              name: p.name,
              verticalImageUrl: p.verticalImageUrl,
            });
          }
        });
      }
      setProjectOptions(initialProjects);
    }
  }, [modalData.open, post, form]);

  const handleRemoveImage = (imageId: string) => {
    setImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      setImages((prev) => {
        const oldIndex = prev.findIndex((img) => img.id === active.id);
        const newIndex = prev.findIndex((img) => img.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleSubmit = async (values: FormValues) => {
    if (!post?.id) return;

    try {
      setIsSubmitting(true);

      const imageIds: string[] = await Promise.all(
        images.map(async (img) => {
          if (img.isNew && img.file) {
            // 새로 추가한 이미지만 업로드
            const { data } = await fileApi.uploadFile(undefined, undefined, img.file);
            return data.data.id || '';
          }
          // 기존 이미지는 ID를 그대로 사용
          return img.id;
        })
      );
      const validImageIds = imageIds.filter((id) => id !== '');

      const requestModel: EditCommunityPostRequestModel = {
        title: values.title,
        content: values.content,
        imageIds: validImageIds,
        taggedProjectId: isRecruitmentPost ? values.taggedProjectId : undefined,
        linkedProjectIds: !isRecruitmentPost ? values.linkedProjectIds : undefined,
      };

      const { data } = await communityPostApi.editCommunityPost(post.id, undefined, requestModel);

      if (data.success) {
        message.success('게시글이 수정되었습니다.');
        form.resetFields();
        setImages([]);
        closeModal();
        onSuccess();
      }
    } catch (e) {
      console.error(e);
      message.error('게시글 수정에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeImage = activeId ? images.find((img) => img.id === activeId) : null;

  return (
    <Modal
      {...modalData}
      title="게시글 수정"
      onOk={form.submit}
      okText="수정"
      cancelText="취소"
      confirmLoading={isSubmitting}
      width={600}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        preserve={false}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        validateMessages={{ required: '필수로 입력해주세요.' }}
      >
        <Form.Item name="title" label="제목" rules={[{ required: true }]}>
          <Input placeholder="제목을 입력해주세요." maxLength={100} showCount />
        </Form.Item>
        <Form.Item name="content" label="내용" rules={[{ required: true }]}>
          <Input.TextArea
            autoSize={{ minRows: 5, maxRows: 10 }}
            placeholder="내용을 입력해주세요."
            maxLength={5000}
            showCount
          />
        </Form.Item>

        <Form.Item label="이미지">
          <div style={{ marginBottom: 8 }}>
            <Upload
              multiple
              beforeUpload={(file) => {
                const newImage: ImageItem = {
                  id: `new-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
                  url: URL.createObjectURL(file),
                  isNew: true,
                  file,
                };
                setImages((prev) => [...prev, newImage]);
                return false;
              }}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>이미지 추가</Button>
            </Upload>
          </div>

          {images.length > 0 && (
            <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <SortableContext items={images.map((img) => img.id)} strategy={rectSortingStrategy}>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    padding: 8,
                    background: '#fafafa',
                    borderRadius: 4,
                    minHeight: 116,
                  }}
                >
                  {images.map((img) => (
                    <SortableImageItem key={img.id} image={img} onRemove={handleRemoveImage} />
                  ))}
                </div>
              </SortableContext>
              <DragOverlay>{activeImage && <ImageOverlay image={activeImage} />}</DragOverlay>
            </DndContext>
          )}

          {images.length === 0 && (
            <div
              style={{
                padding: 16,
                background: '#fafafa',
                borderRadius: 4,
                textAlign: 'center',
                color: '#999',
              }}
            >
              이미지가 없습니다.
            </div>
          )}
        </Form.Item>

        {isRecruitmentPost && (
          <Form.Item name="taggedProjectId" label="태그 프로젝트">
            <Select
              placeholder="태그할 프로젝트를 선택해주세요."
              allowClear
              showSearch
              filterOption={false}
              onSearch={debouncedSearch}
              loading={isSearchingProjects}
              notFoundContent={isSearchingProjects ? '검색 중...' : '검색 결과가 없습니다.'}
              optionLabelProp="label"
            >
              {projectOptions.map((project) => (
                <Select.Option key={project.id} value={project.id} label={project.name}>
                  <ProjectOptionLabel project={project} />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {!isRecruitmentPost && (
          <Form.Item name="linkedProjectIds" label="링크 프로젝트">
            <Select
              mode="multiple"
              placeholder="링크할 프로젝트를 선택해주세요."
              allowClear
              showSearch
              filterOption={false}
              onSearch={debouncedSearch}
              loading={isSearchingProjects}
              notFoundContent={isSearchingProjects ? '검색 중...' : '검색 결과가 없습니다.'}
              optionLabelProp="label"
            >
              {projectOptions.map((project) => (
                <Select.Option key={project.id} value={project.id} label={project.name}>
                  <ProjectOptionLabel project={project} />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
